// components/AdminChat.jsx
import React, { useState, useEffect } from 'react'; // ✅ Removed unused 'useRef'
import { io } from 'socket.io-client';
import {
    MainContainer,
    ChatContainer,
    MessageList,
    Message,
    MessageInput,
    Avatar,
    ConversationHeader,
    TypingIndicator
} from '@chatscope/chat-ui-kit-react';
import '@chatscope/chat-ui-kit-styles/dist/default/styles.min.css';
import {
    Box, Typography, List, ListItem,
    ListItemAvatar, ListItemText, Avatar as MuiAvatar,
    Badge
} from '@mui/material'; // ✅ Removed unused 'Paper' and 'Divider'
import { Person, Circle } from '@mui/icons-material';

const SOCKET_URL = process.env.REACT_APP_SOCKET_URL || 'http://localhost:6001';

function AdminChat() {
    const [socket, setSocket] = useState(null);
    const [conversations, setConversations] = useState({});
    const [activeCustomer, setActiveCustomer] = useState(null);
    const [messages, setMessages] = useState([]);
    // ✅ Removed unused 'isConnected'
    const [isTyping, setIsTyping] = useState(false);

    useEffect(() => {
        const newSocket = io(SOCKET_URL, {
            transports: ['websocket', 'polling']
        });
        setSocket(newSocket);

        newSocket.on('connect', () => {
            console.log('✅ Admin connected');
            newSocket.emit('identify', { role: 'admin' });
        });

        newSocket.on('disconnect', () => {
            console.log('❌ Admin disconnected');
        });

        newSocket.on('conversations_list', (convs) => {
            const convMap = {};
            convs.forEach(conv => {
                convMap[conv.customerId] = conv;
            });
            setConversations(convMap);
        });

        newSocket.on('new_customer', (data) => {
            setConversations(prev => ({
                ...prev,
                [data.customerId]: data.conversation
            }));
        });

        newSocket.on('receive_message', (data) => {
            const { customerId, ...message } = data;

            setConversations(prev => {
                const conv = prev[customerId];
                if (conv) {
                    return {
                        ...prev,
                        [customerId]: {
                            ...conv,
                            messages: [...conv.messages, message]
                        }
                    };
                }
                return prev;
            });

            if (activeCustomer === customerId) {
                setMessages(prev => [...prev, {
                    message: message.text,
                    sentTime: message.time || 'just now',
                    sender: 'Customer',
                    direction: 'incoming'
                }]);
            }
        });

        newSocket.on('customer_typing', (data) => {
            if (activeCustomer === data.customerId) {
                setIsTyping(data.isTyping);
            }
        });

        return () => {
            newSocket.close();
        };
    }, [activeCustomer]); // ✅ Added 'activeCustomer' to dependency array

    const handleSelectCustomer = (customerId) => {
        setActiveCustomer(customerId);
        const conv = conversations[customerId];
        const msgs = conv?.messages || [];
        setMessages(msgs.map(msg => ({
            message: msg.text,
            sentTime: msg.time || 'just now',
            sender: msg.sender === 'admin' ? 'Admin' : 'Customer',
            direction: msg.sender === 'admin' ? 'outgoing' : 'incoming'
        })));
    };

    const handleSend = (message) => {
        if (!message || !socket || !activeCustomer) return;

        const messageData = {
            message: message,
            customerId: activeCustomer,
            sender: 'admin'
        };

        socket.emit('send_message', messageData);

        setMessages(prev => [...prev, {
            message: message,
            sentTime: 'just now',
            sender: 'Admin',
            direction: 'outgoing'
        }]);
    };

    const customerList = Object.keys(conversations);

    return (
        <Box sx={{ display: 'flex', height: '600px', bgcolor: '#f5f5f5', borderRadius: 2, overflow: 'hidden' }}>
            {/* Customer List */}
            <Box sx={{ width: 300, bgcolor: 'white', borderRight: '1px solid #e0e0e0', overflow: 'auto' }}>
                <Box sx={{ p: 2, bgcolor: '#1a1a2e', color: 'white' }}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                        💬 Live Chats
                    </Typography>
                    <Typography variant="caption">
                        {customerList.length} active conversations
                    </Typography>
                </Box>
                <List>
                    {customerList.map((customerId) => {
                        const conv = conversations[customerId];
                        const lastMsg = conv?.messages[conv.messages.length - 1];
                        return (
                            <ListItem
                                key={customerId}
                                button
                                selected={activeCustomer === customerId}
                                onClick={() => handleSelectCustomer(customerId)}
                                sx={{
                                    borderBottom: '1px solid #f0f0f0',
                                    bgcolor: activeCustomer === customerId ? '#e8f0fe' : 'transparent',
                                }}
                            >
                                <ListItemAvatar>
                                    <MuiAvatar sx={{ bgcolor: '#667eea' }}>
                                        <Person />
                                    </MuiAvatar>
                                </ListItemAvatar>
                                <ListItemText
                                    primary={customerId.replace('customer_', 'User ').substring(0, 12)}
                                    secondary={
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Circle sx={{ fontSize: 8, color: 'green' }} />
                                            <Typography variant="caption" noWrap sx={{ maxWidth: 150 }}>
                                                {lastMsg?.text || 'No messages yet'}
                                            </Typography>
                                        </Box>
                                    }
                                />
                                <Badge badgeContent={conv?.messages?.length || 0} color="primary" />
                            </ListItem>
                        );
                    })}
                    {customerList.length === 0 && (
                        <Box sx={{ p: 3, textAlign: 'center', color: 'text.secondary' }}>
                            <Typography variant="body2">No active customers</Typography>
                        </Box>
                    )}
                </List>
            </Box>

            {/* Chat Area */}
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                {activeCustomer ? (
                    <MainContainer style={{ height: '100%' }}>
                        <ChatContainer>
                            <ConversationHeader>
                                <ConversationHeader.Back />
                                <Avatar name={activeCustomer} />
                                <ConversationHeader.Content>
                                    <span style={{ fontWeight: 'bold' }}>
                                        {activeCustomer.replace('customer_', 'User ')}
                                    </span>
                                    <span style={{ fontSize: '0.8rem', color: '#4CAF50' }}>● Online</span>
                                </ConversationHeader.Content>
                            </ConversationHeader>

                            <MessageList
                                typingIndicator={
                                    isTyping ? <TypingIndicator content="Customer is typing..." /> : null
                                }
                            >
                                {messages.map((msg, i) => (
                                    <Message
                                        key={i}
                                        model={{
                                            message: msg.message,
                                            sentTime: msg.sentTime,
                                            sender: msg.sender,
                                            direction: msg.direction,
                                            position: 'single'
                                        }}
                                    >
                                        <Avatar name={msg.sender} />
                                    </Message>
                                ))}
                            </MessageList>

                            <MessageInput
                                placeholder="Type a message..."
                                onSend={handleSend}
                                attachButton={false}
                            />
                        </ChatContainer>
                    </MainContainer>
                ) : (
                    <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'text.secondary' }}>
                        <Box sx={{ textAlign: 'center' }}>
                            <div style={{ fontSize: 60, color: '#ccc' }}>💬</div>
                            <Typography variant="h6" sx={{ mt: 2 }}>
                                Select a conversation
                            </Typography>
                            <Typography variant="body2">
                                Choose a customer from the left to start chatting
                            </Typography>
                        </Box>
                    </Box>
                )}
            </Box>
        </Box>
    );
}

export default AdminChat;